import { Fragment, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Dropdown, Input, Button, Empty } from 'antd';
import type { InputRef } from 'antd';
import { PlusOutlined, SearchOutlined, DownOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import ProjectCreateModal from '../projects/ProjectCreateModal';
import OrganizationCreateModal from '../../pages/ProfilePage/components/OrganizationCreateModal';

import {
  useActiveOrganization,
  useActiveProject,
  useDisplayResponseMessage,
  useOrganizations,
  useRefetchOptions,
  useSetActiveOrganization,
  useSetActiveProject,
  useUser,
  useHasWritePermission,
} from '@hooks';

import { QUERY_KEYS, ROUTES } from '@config';
import {
  useActiveProjectQuery,
  useCreateProjectMutation,
  useProjectsListQuery,
} from './HeaderSelects.query';

import styles from './OrganizationProjectSelect.module.scss';

type ProjectItem = { uuid: string; name: string };

const OrganizationProjectSelect = () => {
  const { _ } = useLingui();
  const [, startTransition] = useTransition();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [searchOrgInput, setSearchOrgInput] = useState('');
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectPage, setProjectPage] = useState(1);
  const [projectsAccumulated, setProjectsAccumulated] = useState<ProjectItem[]>([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const searchOrgInputRef = useRef<InputRef>(null);
  const searchProjectInputRef = useRef<InputRef>(null);

  const organizations = useOrganizations();
  const activeOrganization = useActiveOrganization();
  const activeProject = useActiveProject();

  const setActiveOrganization = useSetActiveOrganization();
  const setActiveProject = useSetActiveProject();
  const displayResponseMessage = useDisplayResponseMessage();
  const refetchOptions = useRefetchOptions();
  const user = useUser();
  const hasWritePermission = useHasWritePermission();
  const queryClient = useQueryClient();

  // Strip a stale ?flow= search param so it doesn't re-hydrate the previous
  // flow selection when the org/project no longer owns it.
  const clearFlowSearchParam = () => {
    const search = new URLSearchParams(location.search);
    if (!search.has('flow')) return;
    search.delete('flow');
    const next = search.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '' },
      { replace: true },
    );
  };

  // Drop cached data scoped to the previous project/flow so the dashboard
  // refetches against the newly selected organization instead of briefly
  // rendering stale rows from keepPreviousData on flow/project list queries.
  const resetScopedQueries = () => {
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.FLOW_CALLS] });
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.FLOWS_LIST] });
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.PROJECTS_LIST] });
  };

  const { mutateAsync: createProject } = useCreateProjectMutation(activeOrganization?.uuid);
  const { data: hydratedProject } = useActiveProjectQuery(activeProject?.uuid);

  const debouncedSetProjectSearch = useMemo(
    () => debounce((value: string) => setProjectSearch(value), 300),
    [],
  );
  useEffect(() => () => debouncedSetProjectSearch.cancel(), [debouncedSetProjectSearch]);

  const lastAppendedRef = useRef<{ search: string; page: number } | null>(null);

  useEffect(() => {
    setProjectPage(1);
    setProjectsAccumulated([]);
    lastAppendedRef.current = null;
  }, [projectSearch, activeOrganization?.uuid]);

  const { data: projectsPage, isFetching: isProjectsFetching, isPreviousData } = useProjectsListQuery({
    organizationUuid: activeOrganization?.uuid,
    search: projectSearch,
    page: projectPage,
  });

  useEffect(() => {
    if (!activeOrganization?.uuid) return;
    if (activeProject?.uuid) return;
    if (isPreviousData) return;
    const first = projectsPage?.results?.[0];
    if (!first) return;
    setActiveProject(first.uuid, first as Project);
  }, [
    activeOrganization?.uuid,
    activeProject?.uuid,
    projectsPage,
    isPreviousData,
    setActiveProject,
  ]);

  useEffect(() => {
    if (isPreviousData) return;
    if (!projectsPage?.results) return;
    const last = lastAppendedRef.current;
    if (last && last.search === projectSearch && last.page === projectPage) return;
    lastAppendedRef.current = { search: projectSearch, page: projectPage };
    if (projectPage === 1) {
      setProjectsAccumulated(projectsPage.results);
    } else {
      setProjectsAccumulated((prev) => {
        const seen = new Set(prev.map((p) => p.uuid));
        return [...prev, ...projectsPage.results.filter((p) => !seen.has(p.uuid))];
      });
    }
  }, [projectsPage, isPreviousData, projectPage, projectSearch]);

  const projectsHasMore = projectsAccumulated.length < (projectsPage?.total_count ?? 0);

  const handleCreateProject = async (name: string, slug?: string) => {
    if (!activeOrganization) {
      displayResponseMessage('projects.create.no_organization');
      return;
    }

    try {
      await createProject({
        name,
        slug,
        organization: activeOrganization.uuid,
      });
      setIsProjectModalOpen(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleCreateOrganizationSuccess = () => {
    refetchOptions();
    setIsOrganizationModalOpen(false);
  };

  const normalizeString = (str: string) => str.toLowerCase().trim();

  const hasOnlyOneOrganization = organizations && organizations.length === 1;

  const filteredOrganizations = organizations?.filter((org) =>
    normalizeString(org.name).includes(normalizeString(searchOrgInput)),
  );

  const handleSelectOrg = (orgUuid: string) => {
    if (orgUuid === activeOrganization?.uuid) {
      setSearchOrgInput('');
      setOrgOpen(false);
      return;
    }
    const next = organizations?.find((o) => o.uuid === orgUuid) ?? null;
    setSearchOrgInput('');
    setOrgOpen(false);
    resetScopedQueries();
    clearFlowSearchParam();
    startTransition(() => {
      setActiveOrganization(orgUuid, next);
      if (
        location.pathname !== ROUTES.FLOWS &&
        location.pathname !== ROUTES.CONNECTIONS &&
        location.pathname !== ROUTES.ANALYTICS
      ) {
        navigate(ROUTES.FLOWS);
      }
    });
  };

  const handleSelectProject = (project: ProjectItem) => {
    if (project.uuid === activeProject?.uuid) {
      setProjectSearchInput('');
      setProjectSearch('');
      setProjectOpen(false);
      return;
    }
    setProjectSearchInput('');
    setProjectSearch('');
    setProjectOpen(false);
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.FLOW_CALLS] });
    clearFlowSearchParam();
    startTransition(() => {
      setActiveProject(project.uuid, project as Project);
      if (location.pathname !== ROUTES.FLOWS && location.pathname !== ROUTES.CONNECTIONS) {
        navigate(ROUTES.FLOWS);
      }
    });
  };

  const projectLabel =
    hydratedProject?.name ?? activeProject?.name ?? _(msg`Select project`);

  return (
    <Fragment>
      <div className={styles.selectContainer}>
        {!hasOnlyOneOrganization && (
          <div className={styles.selectGroup}>
            <label className={styles.selectLabel}>{_(msg`Organization`)}</label>
            <Dropdown
              open={orgOpen}
              onOpenChange={(isOpen) => {
                setOrgOpen(isOpen);
                if (isOpen) setTimeout(() => searchOrgInputRef.current?.focus(), 0);
              }}
              trigger={['click']}
              placement="bottomLeft"
              dropdownRender={() => (
                <div className={styles.dropdownCard} data-testid="organization-select-dropdown">
                  {user?.is_superuser && (
                    <>
                      <div className={styles.createButtonWrapper}>
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          size="small"
                          className={styles.createButton}
                          onClick={() => {
                            setSearchOrgInput('');
                            setOrgOpen(false);
                            setIsOrganizationModalOpen(true);
                          }}
                        >
                          {_(msg`Create Organization`)}
                        </Button>
                      </div>
                      <div className={styles.dropdownDivider} />
                    </>
                  )}
                  <div className={styles.dropdownSearch}>
                    <Input
                      ref={searchOrgInputRef}
                      placeholder={_(msg`Search organizations...`)}
                      prefix={<SearchOutlined />}
                      value={searchOrgInput}
                      onChange={(e) => setSearchOrgInput(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.dropdownDivider} />
                  <div className={styles.dropdownList}>
                    {filteredOrganizations?.map((org) => (
                      <div
                        key={org.uuid}
                        className={styles.dropdownItem}
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => handleSelectOrg(org.uuid)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectOrg(org.uuid); }}
                      >
                        <span className={styles.itemName}>{org.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            >
              <Button className={styles.selectButton} data-testid="organization-select">
                <span className={styles.selectText}>{activeOrganization?.name || _(msg`Select organization`)}</span>
                <DownOutlined className={styles.dropdownIcon} />
              </Button>
            </Dropdown>
          </div>
        )}
        <div className={styles.selectGroup}>
          <label className={styles.selectLabel}>{_(msg`Project`)}</label>
          <Dropdown
            open={projectOpen}
            onOpenChange={(isOpen) => {
              setProjectOpen(isOpen);
              if (isOpen) setTimeout(() => searchProjectInputRef.current?.focus(), 0);
            }}
            trigger={['click']}
            placement="bottomLeft"
            dropdownRender={() => (
              <div className={styles.dropdownCard} data-testid="project-select-dropdown">
                {hasWritePermission && (
                  <>
                    <div className={styles.createButtonWrapper}>
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        size="small"
                        className={styles.createButton}
                        disabled={!activeOrganization}
                        onClick={() => {
                          setProjectSearchInput('');
                          setProjectSearch('');
                          setProjectOpen(false);
                          setIsProjectModalOpen(true);
                        }}
                      >
                        {_(msg`Create Project`)}
                      </Button>
                    </div>
                    <div className={styles.dropdownDivider} />
                  </>
                )}
                <div className={styles.dropdownSearch}>
                  <Input
                    ref={searchProjectInputRef}
                    placeholder={_(msg`Search projects...`)}
                    prefix={<SearchOutlined />}
                    value={projectSearchInput}
                    onChange={(e) => {
                      setProjectSearchInput(e.target.value);
                      debouncedSetProjectSearch(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className={styles.dropdownDivider} />
                <div className={styles.dropdownList}>
                  {projectsAccumulated.length > 0
                    ? projectsAccumulated.map((project) => (
                        <div
                          key={project.uuid}
                          className={styles.dropdownItem}
                          role="menuitem"
                          tabIndex={0}
                          onClick={() => handleSelectProject(project)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectProject(project); }}
                        >
                          <span className={styles.itemName}>{project.name}</span>
                        </div>
                      ))
                    : !isProjectsFetching && (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={_(msg`No projects found`)} />
                      )
                  }
                  {projectsHasMore && (
                    <div className={styles.dropdownItem}>
                      <Button
                        type="link"
                        size="small"
                        block
                        loading={isProjectsFetching}
                        onClick={() => setProjectPage((p) => p + 1)}
                      >
                        <Trans>Load more</Trans>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          >
            <Button
              className={styles.selectButton}
              disabled={!activeOrganization}
              data-testid="project-select"
            >
              <span className={styles.selectText}>{projectLabel}</span>
              <DownOutlined className={styles.dropdownIcon} />
            </Button>
          </Dropdown>
        </div>
      </div>

      <OrganizationCreateModal
        visible={isOrganizationModalOpen}
        onClose={() => setIsOrganizationModalOpen(false)}
        onSuccess={handleCreateOrganizationSuccess}
      />
      <ProjectCreateModal
        open={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onSubmit={handleCreateProject}
      />
    </Fragment>
  );
};

export default OrganizationProjectSelect;
